import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { labelsApi, instancesApi } from '../api/client'

export function useLabels() {
  return useQuery({
    queryKey: ['labels'],
    queryFn: labelsApi.list,
    staleTime: 30_000,
  })
}

export function useCreateLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; color: string }) => labelsApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['labels'] }),
  })
}

export function useUpdateLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name, color }: { id: string; name: string; color: string }) =>
      labelsApi.update(id, { name, color }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labels'] })
      qc.invalidateQueries({ queryKey: ['instances'] })
    },
  })
}

export function useDeleteLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => labelsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labels'] })
      qc.invalidateQueries({ queryKey: ['instances'] })
    },
  })
}

export function useReorderLabels() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => labelsApi.reorder(ids),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: ['labels'] })
      const prev = qc.getQueryData<import('../api/client').InstanceLabel[]>(['labels'])
      if (prev) {
        const byId = new Map(prev.map((l) => [l.id, l]))
        qc.setQueryData(['labels'], ids.map((id) => byId.get(id)).filter(Boolean))
      }
      return { prev }
    },
    onError: (_e, _ids, ctx) => {
      if (ctx?.prev) qc.setQueryData(['labels'], ctx.prev)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['labels'] }),
  })
}

export function useSetInstanceLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ instanceId, labelId }: { instanceId: string; labelId: string | null }) =>
      instancesApi.setLabel(instanceId, labelId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances'] }),
  })
}
